<template>
  <div class="stats-session stack tight">
    <UiPanel>
      <div class="header">
        <div>
          <h3 class="title">Stats Snapshot</h3>
          <div class="subtitle text-muted">Vitals, attributes, and effects.</div>
        </div>
        <div class="header-right">
          <UiChip variant="muted" class="stat-mono num">{{ ICONS.toasts.info }} {{ formatNumber(activeEffects.length) }} effects</UiChip>
          <UiButton size="sm" variant="secondary" @click="emit('open-session', 'skills')">
            <template #icon>{{ ICONS.sessions.skills }}</template>
            Skills
          </UiButton>
        </div>
      </div>
    </UiPanel>

    <UiPanel class="character-panel">
      <div class="character-header">
        <div>
          <div class="character-name">{{ characterName }}</div>
          <div class="character-meta text-muted">{{ characterClassId }} • {{ areaName }}</div>
        </div>
        <UiChip
          v-if="freshnessChip"
          :variant="freshnessChip.variant"
          class="stat-mono num"
        >
          {{ freshnessChip.icon }} {{ freshnessChip.label }}
        </UiChip>
      </div>
      <div class="character-activity">
        <UiChip variant="muted">Now doing</UiChip>
        <div class="character-activity__text">{{ currentActivityLabel }}</div>
      </div>
    </UiPanel>

    <UiPanel>
      <UiSectionHeader>
        <template #title>Vitals</template>
      </UiSectionHeader>

      <div class="vitals">
        <div class="vital">
          <div class="vital-head">
            <span class="vital-label">HP</span>
            <span class="vital-value stat-mono num">{{ formatHpWithTemp(hp, tempHp) }} / {{ formatNumber(maxHp) }}</span>
          </div>
          <UiProgressBar :value="hpPercent" tone="combat" />
        </div>
        <div class="vital">
          <div class="vital-head">
            <span class="vital-label">Mana</span>
            <span class="vital-value stat-mono num">{{ formatNumber(mana) }} / {{ formatNumber(maxMana) }}</span>
          </div>
          <UiProgressBar :value="manaPercent" tone="social" />
        </div>
      </div>
    </UiPanel>

    <UiPanel>
      <UiSectionHeader>
        <template #title>Attributes</template>
      </UiSectionHeader>
      <div class="attr-grid">
        <div v-for="entry in attrEntries" :key="entry.key" class="attr">
          <div class="attr-key">{{ entry.label }}</div>
          <div class="attr-values">
            <div class="attr-val stat-mono num">{{ formatNumber(entry.base) }}</div>
            <div v-if="entry.modifier !== 0" class="attr-mod stat-mono num">
              {{ entry.modifier > 0 ? `+${formatNumber(entry.modifier)}` : formatNumber(entry.modifier) }}
            </div>
          </div>
        </div>
      </div>
    </UiPanel>

    <UiPanel>
      <UiSectionHeader>
        <template #title>Proficiencies</template>
      </UiSectionHeader>

      <div class="proficiencies">
        <div class="proficiency-line stat-mono">Armor: {{ armorProficiencyText }}</div>
        <div class="proficiency-line stat-mono">Weapons: {{ weaponProficiencyText }}</div>
      </div>
    </UiPanel>

    <UiPanel>
      <UiSectionHeader>
        <template #title>Effects</template>
      </UiSectionHeader>

      <div v-if="activeEffects.length === 0" class="text-muted">No active effects.</div>

      <div v-else class="effects">
        <div v-for="effect in activeEffects" :key="effectKey(effect)" class="effect">
          <UiChip :variant="effect.magnitude >= 0 ? 'accent' : 'danger'">
            {{ formatEffectLabel(effect) }}
          </UiChip>
          <div class="text-muted effect-meta">
            Next decay {{ formatRelative(effect.nextDecayAt) }}
          </div>
        </div>
      </div>
    </UiPanel>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { ActiveEffectEntry, Attributes } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import { formatHpWithTemp, formatNumber } from "../../../lib/format";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiProgressBar from "../../../components/ui/UiProgressBar.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";
import UiButton from "../../../components/ui/UiButton.vue";

const props = defineProps<{
  characterName: string;
  characterClassId: string;
  areaName: string;
  currentActivityLabel: string;
  freshnessChip?: {
    variant: "default" | "accent" | "muted" | "warning" | "danger" | "idle" | "active" | "paused" | "blocked";
    label: string;
    icon: string;
  } | null;
  hp: number;
  maxHp: number;
  tempHp: number;
  mana: number;
  maxMana: number;
  attributes: Attributes;
  attributeModifiers?: Partial<Record<keyof Attributes, number>> | null;
  activeEffects: ActiveEffectEntry[];
  armorProficiencies?: string[] | null;
  weaponProficiencies?: string[] | null;
}>();

const emit = defineEmits<{
  "open-session": [id: "skills" | "inventory" | "equipment"];
}>();

const hpPercent = computed(() => (props.maxHp ? Math.round((props.hp / props.maxHp) * 100) : 0));
const manaPercent = computed(() => (props.maxMana ? Math.round((props.mana / props.maxMana) * 100) : 0));

function formatProficiency(label: string): string {
  if (!label) return "";
  return label.slice(0, 1).toUpperCase() + label.slice(1);
}

const armorProficiencyText = computed(() => {
  const raw = (props.armorProficiencies ?? []).filter(Boolean);
  if (!raw.length) return "—";
  return raw.map(formatProficiency).join(", ");
});

const weaponProficiencyText = computed(() => {
  const raw = (props.weaponProficiencies ?? []).filter(Boolean);
  if (!raw.length) return "—";
  return raw.map(formatProficiency).join(", ");
});

const attrEntries = computed(() => {
  const baseEntries = Object.entries(props.attributes || {}) as Array<[keyof Attributes, number]>;
  const mods = props.attributeModifiers || {};
  return baseEntries
    .map(([k, v]) => {
      const base = Math.floor(Number(v || 0));
      const modifier = Math.floor(Number((mods as any)[k] || 0));
      return {
        key: String(k),
        label: String(k).toUpperCase(),
        base,
        modifier
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
});

function effectKey(effect: ActiveEffectEntry): string {
  return `${effect.kind}-${effect.key}-${effect.magnitude}-${effect.nextDecayAt}`;
}

function formatEffectLabel(effect: ActiveEffectEntry): string {
  const magnitude = Math.floor(Number(effect.magnitude || 0));
  const sign = magnitude >= 0 ? `+${magnitude}` : `${magnitude}`;
  if (effect.kind === "attr") return `${String(effect.key).toUpperCase()} ${sign}`;
  const raw = String(effect.key || "");
  const label = raw ? raw.slice(0, 1).toUpperCase() + raw.slice(1) : "Skill";
  return `${label} ${sign}`;
}

function formatRelative(iso: string): string {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return "—";
  const deltaMs = ts - Date.now();
  const abs = Math.abs(deltaMs);
  const sign = deltaMs >= 0 ? "in " : "";
  if (abs < 60_000) return `${sign}${Math.max(0, Math.round(abs / 1000))}s`;
  if (abs < 3_600_000) return `${sign}${Math.round(abs / 60_000)}m`;
  return `${sign}${Math.round(abs / 3_600_000)}h`;
}
</script>

<style scoped>
.stats-session {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  align-items: flex-start;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.header-right {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.character-panel {
  display: grid;
  gap: var(--space-sm);
}

.character-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-md);
}

.character-name {
  font-family: var(--font-display);
  font-size: 1.1rem;
  letter-spacing: 0.02em;
}

.character-meta {
  font-size: 0.85rem;
  margin-top: 0.1rem;
}

.character-activity {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
}

.character-activity__text {
  font-weight: 600;
  color: var(--fg-0);
}

.vitals {
  display: grid;
  gap: var(--space-md);
}

.vital-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--space-sm);
  font-family: var(--font-mono);
}

.vital-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.8rem;
  color: var(--fg-1);
}

.vital-value {
  font-weight: 700;
  color: var(--fg-0);
}

.attr-grid {
  margin-top: var(--space-sm);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-sm);
}

.proficiencies {
  margin-top: var(--space-sm);
  display: grid;
  gap: var(--space-xs);
}

.proficiency-line {
  font-weight: 650;
  color: var(--fg-0);
}

.attr {
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  border: 1px solid var(--border-0);
  border-radius: var(--radius-1);
  padding: 0.6rem 0.75rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--space-sm);
}

.attr-key {
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.attr-val {
  font-weight: 750;
}

.attr-values {
  display: flex;
  align-items: baseline;
  gap: var(--space-sm);
  justify-content: flex-end;
  text-align: right;
}

.attr-mod {
  font-weight: 650;
  color: var(--color-warning);
}

.effects {
  margin-top: var(--space-sm);
  display: grid;
  gap: var(--space-sm);
}

.effect {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
}

.effect-meta {
  font-size: 0.85rem;
  font-family: var(--font-mono);
}

@media (max-width: 720px) {
  .attr-grid {
    grid-template-columns: 1fr;
  }
}
</style>
